import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'

import type { StockInNotePrintRow } from '@/lib/stock-in-note-print'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** Một dòng kg của phiếu cần hỏi số bao, kèm vị trí của nó trong `rows`. */
export interface BagsDialogRow {
    index: number
    row: StockInNotePrintRow
}

// Factory để message i18n được đánh giá lazily (rule Forms của repo).
const createBagsSchema = () =>
    z.object({
        bags: z.array(
            z.object({
                // Để trống được: lô lẻ / không đóng bao thì phiếu in "—".
                // Đã nhập thì phải là số nguyên dương.
                value: z
                    .number({ message: m.sinp_bags_err_invalid() })
                    .int(m.sinp_bags_err_invalid())
                    .positive(m.sinp_bags_err_invalid())
                    .optional(),
            }),
        ),
    })

type BagsValues = z.infer<ReturnType<typeof createBagsSchema>>

/** Ô số bỏ trống → undefined để zod báo "chưa nhập" thay vì hiểu thành 0. */
function emptyToUndefined(v: unknown): number | undefined {
    if (v === '' || v === null || v === undefined) return undefined
    const n = Number(v)
    return Number.isNaN(n) ? undefined : n
}

interface Props {
    /** Các dòng kg cần nhập số bao; rỗng/null = hộp thoại đóng. */
    rows: BagsDialogRow[] | null
    onCancel: () => void
    /** Số bao theo đúng thứ tự `rows`; `undefined` = dòng để trống. */
    onConfirm: (bags: Array<number | undefined>) => void
}

/**
 * M-196: hỏi số bao của từng dòng tính theo kg trước khi in phiếu nhập kho, để
 * phiếu giấy ghi rõ lô đó đóng bao nhiêu bao. Số bao chỉ in ra, không lưu.
 */
export function StockInNoteBagsDialog({ rows, onCancel, onConfirm }: Props) {
    return (
        <Dialog
            open={!!rows?.length}
            onOpenChange={(open) => {
                if (!open) onCancel()
            }}
        >
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.sinp_bags_title()}</DialogTitle>
                    <DialogDescription>{m.sinp_bags_desc()}</DialogDescription>
                </DialogHeader>
                {/* Form dựng lại mỗi lần mở để không giữ số của phiếu trước. */}
                {rows?.length ? (
                    <BagsForm
                        rows={rows}
                        onCancel={onCancel}
                        onConfirm={onConfirm}
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    )
}

function BagsForm({
    rows,
    onCancel,
    onConfirm,
}: {
    rows: BagsDialogRow[]
    onCancel: () => void
    onConfirm: (bags: Array<number | undefined>) => void
}) {
    const form = useForm<BagsValues>({
        resolver: zodResolver(createBagsSchema()),
        defaultValues: {
            bags: rows.map(() => ({ value: undefined })),
        },
    })
    const submit = form.handleSubmit((values) =>
        onConfirm(values.bags.map((b) => b.value)),
    )

    return (
        <FormProvider {...form}>
            <form onSubmit={submit} noValidate className="space-y-4">
                <div className="max-h-[60vh] space-y-3 overflow-y-auto">
                    {rows.map(({ index, row }, i) => {
                        const name = `bags.${i}.value`
                        return (
                            <Field
                                key={index}
                                name={name}
                                label={m.sinp_bags_row_label({
                                    lot: row.lotCode || '—',
                                    name: row.name || '—',
                                    quantity: formatQty(row.quantity),
                                    unit: row.unit.trim(),
                                })}
                            >
                                <Input
                                    id={name}
                                    type="number"
                                    inputMode="numeric"
                                    step="1"
                                    min="1"
                                    placeholder={m.sinp_bags_placeholder()}
                                    autoFocus={i === 0}
                                    {...form.register(`bags.${i}.value`, {
                                        setValueAs: emptyToUndefined,
                                    })}
                                />
                            </Field>
                        )
                    })}
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onCancel}>
                        {m.common_cancel()}
                    </Button>
                    {/* Không disable vì form chưa hợp lệ: bấm được để thấy lỗi. */}
                    <Button type="submit">{m.sinp_bags_print()}</Button>
                </DialogFooter>
            </form>
        </FormProvider>
    )
}
